import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Check } from 'lucide-react';
import { companyInfo } from '../data';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function Profile() {
  useDocumentTitle('Profil');
  return (
    <div className="w-full bg-neutral-50 min-h-screen">
      {/* Page Header */}
      <section className="bg-neutral-950 text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/ce352cec-9ea8-4fdc-a3ec-5ad10b3fe31c.jpg"
            alt="Murerarbejde"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="w-16 h-1 bg-red-700 mb-4" />
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight">Profil</h1>
        </div>
      </section>

      {/* About Section (Split Layout) */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-red-700">Min Historie</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-neutral-950 tracking-tight">
              Klassisk murerarbejde udført med stolthed siden starten
            </h2>
            <p className="text-neutral-700 leading-relaxed">
              Pedersen Byg blev grundlagt med en klar vision: At levere murerarbejde af så høj kvalitet, at det kan holde i generationer, og samtidig være den mest troværdige og nærværende murerpartner for borgerne i Langå og omegn.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Jeg ved, at det at få udført murerarbejde på sit hjem er en tillidssag. Derfor lægger jeg stor vægt på grundig rådgivning inden arbejdet går i gang, gennemsigtige priser og en tæt dialog undervejs. Jeg overholder altid mine aftaler, og jeg rydder altid pænt op efter mig selv – det kalder jeg ordentligt håndværk.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Som murermester har jeg specialiseret mig i <strong>facade- og murværk</strong>. Det danske klima stiller store krav til murværkets modstandsdygtighed, og jeg har den rette ekspertise til at sikre, at dit hus både ser smukt ud og er optimalt beskyttet mod vind og vejr.
            </p>
          </div>

          {/* Right: Image & Quick Stats */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative rounded-sm overflow-hidden border border-neutral-200 shadow-md">
              <img
                src="https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/ed368980-b8e5-46d3-a3b2-459db4c15b56.jpg"
                alt="Klassisk murerarbejde i høj kvalitet"
                className="w-full h-80 md:h-[400px] object-cover object-top"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-neutral-950/90 to-transparent p-6 text-white">
                <p className="text-xs text-white font-bold uppercase tracking-wider">Kvalitet & Præcision</p>
                <p className="text-lg font-bold">Pedersen Byg</p>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center py-2">
                <p className="text-3xl font-extrabold text-red-700">6</p>
                <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold mt-1">Års Erfaring</p>
              </div>
              <div className="text-center py-2">
                <p className="text-3xl font-extrabold text-neutral-950">100%</p>
                <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold mt-1">Lokalt Forankret</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Guarantee Section */}
      <section className="bg-neutral-100 py-16 md:py-24 border-y border-neutral-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-4">
              <div className="flex items-center gap-2 text-red-700">
                <ShieldCheck size={32} />
                <h3 className="text-2xl font-extrabold text-neutral-950 tracking-tight">Din tryghed for ordentligt arbejde</h3>
              </div>
              <p className="text-neutral-700 leading-relaxed">
                Hos Pedersen Byg går jeg op i høj kvalitet og solidt håndværk. Jeg sikrer, at alt murerarbejde udføres efter de højeste standarder, så du får et holdbart og smukt resultat, du kan glæde dig over i mange år.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-neutral-600 font-medium">
                <li className="flex items-center gap-2">
                  <Check size={18} className="text-neutral-950 stroke-[3]" />
                  <span>Kvalitetsmaterialer</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={18} className="text-neutral-950 stroke-[3]" />
                  <span>Sikret mod sjusk og fejl</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={18} className="text-neutral-950 stroke-[3]" />
                  <span>Gælder for alle opgaver</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check size={18} className="text-neutral-950 stroke-[3]" />
                  <span>Din ultimative tryghed</span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-4 flex justify-center lg:justify-end">
              <Link
                to="/kontakt"
                className="bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg shadow-md text-center w-full lg:w-auto"
              >
                Få et tilbud i dag
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
